import { type AgentSession, getAgentSession } from 'api/agentSessions/agentSessions.api';
import { generateProjectSuggestion } from 'api/agentSessions/projectSuggestions.api';
import { useRef, useState } from 'react';
import { canPredictProject } from './AgentProjectPredictions';
import {
  type AgentPeriod,
  groupPeriodsBySession,
  type SessionGroup,
  type SessionSort,
  sortTrackedSessions,
} from './agentTracking.model';
import { assignErrorMessage } from './projectAssignmentError';

export const bulkPredictionFetchers = { generateProjectSuggestion, getAgentSession };
type Fetchers = typeof bulkPredictionFetchers;

/** Match the group and session row order used by AgentTrackingSessions. */
export function periodsInViewOrder(groups: SessionGroup[], sort: SessionSort): AgentPeriod[] {
  return groups.flatMap(group =>
    sortTrackedSessions(groupPeriodsBySession(group.periods), sort).flatMap(
      session => session.periods
    )
  );
}

/** Selected periods that still need a project and have no saved suggestion yet. */
export function bulkPredictionTargets(periods: AgentPeriod[]) {
  return periods.filter(p => p.projectId === null && !p.projectSuggestion && canPredictProject(p));
}

/**
 * Predict each period, a few at a time, in the supplied view order.
 * A session is refetched once its periods settle,
 * so its row shows the new suggestions; the refetch runs through `changeSession`,
 * in turn with the page's other session changes, so it cannot bring back a
 * project from before an assignment confirmed meanwhile. Predicting never
 * assigns a project. The first failure stops new requests; finished work is
 * still refreshed.
 */
export async function predictProjects(
  targets: AgentPeriod[],
  {
    fetchers,
    changeSession,
    onProgress,
    concurrency = 4,
  }: {
    fetchers: Fetchers;
    changeSession: (request: () => Promise<AgentSession>) => Promise<AgentSession>;
    onProgress?: (done: number) => void;
    concurrency?: number;
  }
) {
  const queue = [...targets];
  const remaining = new Map<number, number>();
  queue.forEach(p => remaining.set(p.sessionId, (remaining.get(p.sessionId) ?? 0) + 1));
  const changed = new Set<number>();
  let next = 0;
  let done = 0;
  let failure: unknown = null;
  const refresh = async (sessionId: number) => {
    changed.delete(sessionId);
    try {
      await changeSession(() => fetchers.getAgentSession(sessionId));
    } catch (e) {
      failure ??= e;
    }
  };
  const run = async () => {
    while (failure === null && next < queue.length) {
      const period = queue[next++];
      try {
        await fetchers.generateProjectSuggestion(period.sessionId, period.segmentId);
        changed.add(period.sessionId);
        done++;
        onProgress?.(done);
      } catch (e) {
        failure ??= e;
      }
      const left = (remaining.get(period.sessionId) ?? 1) - 1;
      remaining.set(period.sessionId, left);
      if (left === 0 && changed.has(period.sessionId)) await refresh(period.sessionId);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, run));
  for (const sessionId of [...changed]) await refresh(sessionId);
  return { predicted: done, failure };
}

export function useBulkProjectPredictions(
  changeSession: ((request: () => Promise<AgentSession>) => Promise<AgentSession>) | undefined,
  fetchers: Fetchers = bulkPredictionFetchers
) {
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);
  const running = useRef(false);
  const start = async (selected: AgentPeriod[]) => {
    const targets = bulkPredictionTargets(selected);
    if (running.current || !changeSession || !targets.length) return;
    running.current = true;
    setNotice(null);
    setProgress({ done: 0, total: targets.length });
    try {
      const { predicted, failure } = await predictProjects(targets, {
        fetchers,
        changeSession,
        onProgress: done => setProgress({ done, total: targets.length }),
      });
      const ready = `Suggestions ready for ${predicted} ${predicted === 1 ? 'period' : 'periods'}. Open a session's project to review.`;
      setNotice(
        failure
          ? {
              text: `${predicted ? `${ready} ` : ''}${assignErrorMessage(failure, 'Prediction')}`,
              error: true,
            }
          : { text: ready, error: false }
      );
    } finally {
      running.current = false;
      setProgress(null);
    }
  };
  return { progress, notice, start, clearNotice: () => setNotice(null) };
}
